import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Box, Stack, TableBody, TableHead, TableRow, Typography, useTheme } from '@mui/material'
import { LockIcon, SlidersHorizontalIcon, TagIcon, XIcon } from 'lucide-react'
import { Alert, Button, Card, Skeleton } from '@repo/ui'
import {
  DEFAULT_REVENUE_PERIOD,
  parseLeadSourceGroup,
  parseRevenuePeriod,
  SALES_STAGE_LABELS,
  type AutoWonPlan,
  type RevenuePeriod,
  LEAD_SOURCE_GROUP_LABELS,
  SALES_STAGES,
  type LeadSourceGroup,
  type PipelineFlow,
} from '@repo/types'

import { EmptyState } from 'components'
import { SankeyChart, type SankeyChartLink, type SankeyChartNode } from 'components/analytics/SankeyChart'
import { AnalyticsBlock } from 'components/display/Analytics/Analytics'
import { KpiTileGrid } from 'components/display/Analytics/KpiTileGrid'
import { InfoTooltip } from 'components/feedback/InfoTooltip'
import { ViewToggle, type ViewToggleOption } from 'components/input/ViewToggle/ViewToggle'
import { sourceChannelLabel } from 'constants/sourceChannels'
import { useSalesAutoWonPreview, useSalesBoard, useSalesFlow } from 'hooks/api/useGtmSalesPipeline'
import { relativeOrDateTime } from 'utils'
import type { SalesBoard, SalesFlow } from 'services'

import { Cell, EmptyRow, HeadCell, SectionNote, StatTable } from '../InternalAnalyticsPage/components/shared'
import { rangeForPreset } from '../InternalAnalyticsPage/websiteAnalyticsModel'
import { DealsBoard, DealsBoardSkeleton } from './DealsBoard'
import { isPipelineRange, MANAGE_SALES_TIP, moneyShort, PIPELINE_RANGE_OPTIONS, STAGE_LABEL, type PipelineRange } from './dealsModel'
import { aggregateNote, ratioLabel } from './ForecastFigures'
import { inPeriod, PERIOD_OPTIONS, periodLabel } from '../GtmChannelsPage/masterPipelineView'
import type { SalesWorkspaceActions } from './useSalesWorkspace'

/** The presets plus All time (shared with GTM → Channels): the board is current state, the flow is a cohort. */
const RANGE_OPTIONS = PIPELINE_RANGE_OPTIONS

type FlowMode = 'count' | 'value'
const FLOW_MODES: readonly ViewToggleOption<FlowMode>[] = [
  { value: 'count', label: 'Deals' },
  { value: 'value', label: 'Value' },
]

/**
 * The KPI tiles. Each explains ITS figure from the glossary (`term`, §44), with the house info
 * glyph; the basis line sits under the number (`unit`), never as a `note` — AnalyticsBlock
 * treats a note as a CAVEAT and marks the glyph, which made every tile here look unlike every
 * other page's (Spencer, 2026-10-13; the same fix SeoGscCard records).
 */
const Tiles = ({ data, rangeLabel }: { data: SalesBoard; rangeLabel: string }) => {
  const k = data.kpis
  const n = (x: number, one: string, many: string) => `${x.toLocaleString('en-US')} ${x === 1 ? one : many}`
  const accuracyUnit = (a: SalesBoard['kpis']['accuracy']['revenue']) =>
    a.n === 0 ? 'no company has both yet' : `${n(a.n, 'company', 'companies')} · median ${ratioLabel(a.median)}`
  const tiles = [
    {
      key: 'open',
      title: 'Open pipeline',
      term: 'sales-open-pipeline',
      stat: moneyShort(k.open_value),
      // VRG-736 — "+ tags": the same deals' tag margin, beside the ARR and never in it.
      unit: `${n(k.open_pipeline_deals, 'deal', 'deals')}${k.open_without_usd_value ? ` · ${k.open_without_usd_value} not counted` : ''}${
        k.open_tag_margin ? ` · + ${moneyShort(k.open_tag_margin)} tags` : ''
      }`,
    },
    { key: 'weighted', title: 'Weighted pipeline', term: 'sales-weighted-value', stat: moneyShort(k.weighted_value), unit: '× stage probability' },
    { key: 'committed-mrr', title: 'Committed MRR', term: 'sales-committed-arr', stat: moneyShort(k.committed_mrr), unit: `${n(k.committed_deals, 'paying deal', 'paying deals')}, live mode` },
    { key: 'committed-arr', title: 'Committed ARR', term: 'sales-committed-arr', stat: moneyShort(k.committed_arr), unit: 'MRR × 12' },
    {
      key: 'forecast-accuracy',
      // Spencer's 'Forecast accuracy (revenue)'. The parenthetical wrapped the title onto two lines at
      // 1280 and pushed the figure off the row's baseline; the table column's name says the same.
      title: 'Revenue accuracy',
      term: 'sales-forecast-accuracy',
      stat: ratioLabel(k.accuracy.revenue.ratio),
      unit: accuracyUnit(k.accuracy.revenue),
    },
    { key: 'roi-accuracy', title: 'ROI accuracy', term: 'brand-roi-accuracy', stat: ratioLabel(k.accuracy.roi.ratio), unit: accuracyUnit(k.accuracy.roi) },
    {
      key: 'deals',
      title: 'Open deals',
      term: 'sales-deal',
      stat: k.open_deals.toLocaleString('en-US'),
      unit: `${k.committed_deals} committed · ${k.lost_deals} lost`,
    },
    {
      key: 'won',
      title: `Won, ${rangeLabel}`,
      term: 'sales-won',
      stat: `${k.won_count} · ${moneyShort(k.won_value)}`,
      unit: k.avg_days_to_paid === null ? 'none in this range' : `${Math.round(k.avg_days_to_paid).toLocaleString('en-US')} days to Won on average`,
    },
  ]
  return (
    // Two rows of four: Vera's money (forecast → contract), then how good the forecast is.
    <KpiTileGrid data-testid="sales-kpi-tiles">
      {tiles.map((t) => (
        <Box key={t.key} data-testid={`sales-kpi-${t.key}`} sx={{ minWidth: 0 }}>
          <AnalyticsBlock title={t.title} term={t.term} stat={t.stat} unit={t.unit} statSize={28} />
        </Box>
      ))}
    </KpiTileGrid>
  )
}

/**
 * VRG-700 — the dry run of "paying deals move to Won automatically", above the board: which
 * deals the next run moves (switched off: would move) and how many it leaves alone. Nothing
 * renders when there is nothing to move, so a quiet board stays quiet.
 */
const AutoWonPreviewNote = ({ plan }: { plan: AutoWonPlan }) => {
  if (plan.candidates.length === 0) return null
  const n = plan.candidates.length
  const deals = `${n} ${n === 1 ? 'deal' : 'deals'}`
  const list = plan.candidates
    .map((c) => `${c.account_name} (${c.reopen ? `${c.closed_reason === 'archived' ? 'archived' : 'lost'}, reopened` : SALES_STAGE_LABELS[c.from_stage]} → Won, ${c.subscription_id}, ${moneyShort(c.arr)} ARR)`)
    .join('; ')
  return (
    <Alert
      severity="info"
      title={plan.enabled ? `${deals} will move to Won at the next run` : `${deals} would move to Won automatically`}
      description={
        <span data-testid="auto-won-preview">
          {plan.enabled
            ? 'A paying subscription moves its deal to Won on the next Stripe event or the nightly reconcile (04:40 UTC): '
            : 'Moving paying deals to Won is switched off (SALES_AUTO_WON_ENABLED). Turned on, the first run moves: '}
          {list}.{plan.skipped.length ? ` ${plan.skipped.length} more with a subscription that is not paying, or overridden by hand, stay where they are.` : ''}
        </span>
      }
    />
  )
}

/** The flow, as SankeyChart nodes and links, in count or value mode (value in the switch's period). */
const flowChart = (flow: PipelineFlow, mode: FlowMode, period: RevenuePeriod): { nodes: SankeyChartNode[]; links: SankeyChartLink[] } => ({
  nodes: flow.nodes.map((n) => ({
    id: n.id,
    label: n.label,
    tone:
      n.kind === 'source'
        ? 'source'
        : n.id === 'stage:paid_client' || n.id === 'end:committed'
          ? 'success'
          : n.kind === 'end'
            ? 'end'
            : 'stage',
    clickable: n.kind === 'source',
    hint: n.kind === 'source' ? 'Click to show only these deals on the board' : undefined,
  })),
  links: flow.links.map((l) => ({ source: l.source, target: l.target, value: mode === 'count' ? l.count : inPeriod(l.value, period) })),
})

const ChannelTable = ({ data }: { data: SalesFlow }) => {
  const total = SALES_STAGES.reduce(
    (acc, s) => ({ ...acc, [s]: data.by_channel.reduce((t, r) => t + r[s], 0) }),
    {} as Record<(typeof SALES_STAGES)[number], number>,
  )
  const committedTotal = data.by_channel.reduce((t, r) => t + r.committed, 0)
  return (
    <StatTable>
      <TableHead>
        <TableRow>
          <HeadCell>
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
              Source channel
              <InfoTooltip term="source-channel" />
            </Box>
          </HeadCell>
          {SALES_STAGES.map((s) => (
            <HeadCell key={s} align="right">
              {STAGE_LABEL[s]}
            </HeadCell>
          ))}
          <HeadCell align="right">
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
              Committed
              <InfoTooltip term="sales-committed-arr" />
            </Box>
          </HeadCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {data.by_channel.length === 0 ? (
          <EmptyRow colSpan={SALES_STAGES.length + 2} text="No deals entered the pipeline in this range." />
        ) : (
          data.by_channel.map((row) => (
            <TableRow key={row.channel ?? (row.expansion ? 'expansion' : 'none')} data-testid={`sales-channel-${row.channel ?? (row.expansion ? 'expansion' : 'none')}`}>
              <Cell strong={row.channel !== null || !!row.expansion}>
                <Box component="span" sx={{ color: row.channel || row.expansion ? 'text.primary' : 'text.secondary', display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                  {/* VRG-701 — Expansion: an existing client's later deal, a source but not a channel. */}
                  {row.expansion ? LEAD_SOURCE_GROUP_LABELS.expansion : sourceChannelLabel(row.channel)}
                  {row.channel === null && <InfoTooltip term={row.expansion ? 'sales-expansion' : 'source-channel-not-recorded'} />}
                </Box>
              </Cell>
              {SALES_STAGES.map((s) => (
                <Cell key={s} align="right">
                  {row[s].toLocaleString('en-US')}
                </Cell>
              ))}
              <Cell align="right">{row.committed.toLocaleString('en-US')}</Cell>
            </TableRow>
          ))
        )}
        <TableRow>
          <Cell strong>Total</Cell>
          {SALES_STAGES.map((s) => (
            <Cell key={s} align="right" strong>
              {total[s].toLocaleString('en-US')}
            </Cell>
          ))}
          <Cell align="right" strong>
            {committedTotal.toLocaleString('en-US')}
          </Cell>
        </TableRow>
      </TableBody>
    </StatTable>
  )
}

const PipelineSkeleton = () => (
  <Stack spacing={6} useFlexGap>
    <KpiTileGrid>
      {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
        <Skeleton key={i} variant="rounded" height={124} sx={{ borderRadius: 3 }} />
      ))}
    </KpiTileGrid>
    <Card title="Deals">
      <Box sx={{ mt: 4 }}>
        <DealsBoardSkeleton />
      </Box>
    </Card>
    <Card title="Pipeline flow">
      <Skeleton variant="rounded" height={360} sx={{ mt: 4 }} />
    </Card>
  </Stack>
)

/**
 * Sales → Pipeline (`/internal/gtm/sales`, the default tab) — VRG-691. The pipeline OWNS
 * everything after a lead, from any channel (Spencer, 2026-10-13): the board of deals, the KPI
 * tiles, the flow from lead sources through the stages, and the by-channel counts.
 *
 * All four read ONE list of deals, folded server-side by the shared functions in `@repo/types`
 * (gtm/sales-pipeline): the board and tiles from `GET …/sales/board`, the flow chart and the
 * by-source table from `GET …/sales/flow` — VRG-696's shared flow query, the same one GTM →
 * Channels' master pipeline reads — over the same snapshot, so they reconcile by construction:
 * the board and the open-pipeline tiles are the current state of every open deal; the flow, the
 * table, Won and Avg days to Paid are the RANGE (deals that entered the pipeline in it). On
 * "All time" the flow's "Still open" and "Demo" drop-offs equal the board's columns.
 */
export const SalesPipelinePanel = ({ actions, canManage }: { actions: SalesWorkspaceActions; canManage: boolean }) => {
  const theme = useTheme()
  const [params, setParams] = useSearchParams()
  // Spencer (2026-10-13): All time by default; a chosen range stays in the URL (`?range=`).
  const preset: PipelineRange = isPipelineRange(params.get('range')) ? (params.get('range') as PipelineRange) : 'all'
  const range = useMemo(() => (preset === 'all' ? null : rangeForPreset(preset)), [preset])
  const setParam = (key: string, value: string | null) => {
    const p = new URLSearchParams(params)
    if (value === null) p.delete(key)
    else p.set(key, value)
    setParams(p, { replace: true })
  }
  const sourceParam = params.get('source')
  // VRG-692 renamed the groups; a link carrying a VRG-691 key (trade_show, outreach, paid) still
  // filters, to the group that replaced it.
  const source: LeadSourceGroup | null = parseLeadSourceGroup(sourceParam)
  const [mode, setMode] = useState<FlowMode>('count')
  // VRG-700 — the MRR/ARR switch for Value mode: ARR by default, `?period=mrr` otherwise (the
  // same parameter and default as GTM → Channels).
  const period = parseRevenuePeriod(params.get('period'))
  const preview = useSalesAutoWonPreview()

  const { data, isLoading, error } = useSalesBoard(range)
  // VRG-696 — the flow chart and the by-source table come from the SHARED flow query, the same
  // one GTM → Channels' master pipeline reads, so the two pages cannot count different deals.
  const flowFilters = useMemo(() => ({ range }), [range])
  const { data: flowData, isLoading: flowLoading } = useSalesFlow(flowFilters)
  const status = (error as (Error & { status?: number }) | null)?.status
  const rangeLabel = preset === 'all' ? 'all time' : (RANGE_OPTIONS.find((o) => o.value === preset)?.label ?? '').toLowerCase()
  const deals = useMemo(() => (data ? (source ? data.deals.filter((d) => d.source_group === source) : data.deals) : []), [data, source])
  const chart = useMemo(() => (flowData ? flowChart(flowData.sales_flow, mode, period) : null), [flowData, mode, period])

  return (
    <Stack spacing={6} useFlexGap>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} spacing={3}>
        <Typography variant="body2" color="text.secondary">
          {range ? `${range.since} to ${range.until} (UTC)` : 'All time'}
        </Typography>
        <ViewToggle options={RANGE_OPTIONS} value={preset} onChange={(v) => setParam('range', v)} ariaLabel="Date range" />
      </Stack>

      {isLoading ? (
        <PipelineSkeleton />
      ) : error || !data ? (
        <Card>
          <EmptyState
            icon={<LockIcon size={28} strokeWidth={1} color={theme.palette.text.secondary} />}
            title={status === 403 ? 'You cannot view the sales pipeline' : 'Could not load the sales pipeline'}
            text={
              status === 403
                ? 'The sales pipeline needs the "View GTM" permission and a Vera staff role.'
                : 'The request failed. Reload the page, or check the API is running.'
            }
            sx={{ py: 10 }}
          />
        </Card>
      ) : (
        <>
          <Tiles data={data} rangeLabel={rangeLabel} />

          {preview.data && <AutoWonPreviewNote plan={preview.data} />}

          <Card
            title="Deals"
            action={
              <Stack direction="row" spacing={3} alignItems="center">
                {source && (
                  <Button variant="secondary" endIcon={<XIcon strokeWidth={1} />} onClick={() => setParam('source', null)}>
                    {LEAD_SOURCE_GROUP_LABELS[source]} only
                  </Button>
                )}
                <Button
                  variant="secondary"
                  startIcon={<SlidersHorizontalIcon strokeWidth={1} />}
                  onClick={actions.openProbabilities}
                  disabled={!canManage}
                  title={canManage ? undefined : MANAGE_SALES_TIP}
                >
                  Stage probabilities
                </Button>
                {/* VRG-736 — the tag unit cost and markup behind the tag-margin line (placeholders). */}
                <Button
                  variant="secondary"
                  startIcon={<TagIcon strokeWidth={1} />}
                  onClick={actions.openTagEconomics}
                  disabled={!canManage}
                  title={canManage ? undefined : MANAGE_SALES_TIP}
                >
                  Tag economics
                </Button>
              </Stack>
            }
          >
            <Stack spacing={3} sx={{ mt: 4 }}>
              <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                {canManage
                  ? 'Drag a deal to move it, or onto Lost to close it. Vera moves deals itself when it sees a waitlist grant, a demo, a real company, or a first paid invoice. A move past what Vera can see asks you to confirm.'
                  : `${MANAGE_SALES_TIP.replace('.', ' to move deals.')}`}
              </Typography>
              <DealsBoard
                deals={deals}
                canManage={canManage}
                onOpen={(d) => actions.openCompany(d.account_id, d.id)}
                onMove={actions.requestMove}
                onLost={actions.requestLost}
                showLost={params.get('lost') === '1'}
                onToggleLost={() => setParam('lost', params.get('lost') === '1' ? null : '1')}
              />
            </Stack>
          </Card>

          <Card
            title="Pipeline flow"
            action={
              <Stack direction="row" spacing={3} alignItems="center">
                {mode === 'value' && (
                  <ViewToggle options={PERIOD_OPTIONS} value={period} onChange={(v) => setParam('period', v === DEFAULT_REVENUE_PERIOD ? null : v)} ariaLabel="Revenue period" />
                )}
                <ViewToggle options={FLOW_MODES} value={mode} onChange={setMode} ariaLabel="Flow measure" />
              </Stack>
            }
          >
            <Stack spacing={4} sx={{ mt: 4 }}>
              {flowLoading && <Skeleton variant="rounded" height={380} />}
              {chart && (
                <SankeyChart
                  nodes={chart.nodes}
                  links={chart.links}
                  height={380}
                  preserveOrder
                  unit={mode === 'count' ? 'deals' : periodLabel(period)}
                  formatValue={mode === 'count' ? (v) => v.toLocaleString('en-US') : (v) => moneyShort(v)}
                  selectedNodeId={source ? `source:${source}` : null}
                  onNodeClick={(n) => {
                    const g = n.id.replace(/^source:/, '')
                    const next = parseLeadSourceGroup(g)
                    setParam('source', next && next !== source ? next : null)
                  }}
                  ariaLabel={`Deals flowing from lead sources through ${SALES_STAGES.map((st) => SALES_STAGE_LABELS[st]).join(', ')}`}
                />
              )}
              {flowData && (
              <SectionNote>
                Deals that entered the pipeline {preset === 'all' ? 'at any time' : `in the last ${rangeLabel}`} ({flowData.sales_flow.cohort}),
                from where they came from, through each stage they reached, to where they are now. A deal that skipped a stage
                skips its node.
                {mode === 'value' && flowData.sales_flow.without_usd_value > 0
                  ? ` ${flowData.sales_flow.without_usd_value} of them have no USD value and do not show in Value.`
                  : ''}{' '}
                Click a source to show only its deals on the board. The whole funnel, by channel and with filters, is on GTM → Channels.
              </SectionNote>
              )}
            </Stack>
          </Card>

          <Card title="By source channel">
            <Stack spacing={4} sx={{ mt: 4 }}>
              {flowData ? <ChannelTable data={flowData} /> : <Skeleton variant="rounded" height={240} />}
              <SectionNote>
                The same deals as the flow chart: each column counts the deals that passed through that stage (a deal can skip
                {SALES_STAGE_LABELS.access_granted} and {SALES_STAGE_LABELS.demo}). Committed counts
                those paying now (a live-mode subscription with a paid invoice above $0 in the last 60 days; never a $0 pilot or test mode) (on All time it equals the Committed tiles' deals). Demo, internal, test and
                example companies are never counted as {SALES_STAGE_LABELS.account_created}.
                {(flowData?.leads_without_company ?? 0) > 0
                  ? ` ${flowData?.leads_without_company} ${flowData?.leads_without_company === 1 ? 'person is' : 'people are'} not on a deal (no company yet); they are on the People tab.`
                  : ''}{' '}
                Computed {relativeOrDateTime(flowData?.computed_at ?? data.computed_at)}.
              </SectionNote>
            </Stack>
          </Card>
        </>
      )}
    </Stack>
  )
}
